'use client'
import { SanityImageSource } from '@sanity/image-url'
import Image from 'next/image'
import Link from 'next/link'
import { getSiteConfig, isUkSite } from '@/lib/config/site'
import { cn } from '@/lib/utils/utils'
import { urlFor } from '@/sanity/lib/image'
import type { SITE_SETTINGS_QUERY_RESULT } from '@/sanity/types'
import { Button } from '../../../common/button'
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger,
  navigationMenuTriggerStyle,
} from '../../../common/navigation-menu'
import RegisterInterestForm from '../../../features/BlockContent/HeroSliderBanner/RegisterInterest'
import MastHeadMobile from './MastHeadMobile'

type MastheadNavigationType =
  NonNullable<SITE_SETTINGS_QUERY_RESULT>['mastheadNavigation']

export type MastHeadRendererCareersProps = {
  logo: SanityImageSource & { altText?: string }
  mastheadNavigation: MastheadNavigationType
  careers?: boolean
}

const careersTriggerStyle =
  'text-lg text-careers-blue-darker hover:text-careers-green'

function MastHeadCareersDesktop({
  logo,
  mastheadNavigation,
  careers,
}: {
  logo: SanityImageSource & { altText?: string }
  mastheadNavigation: MastheadNavigationType
  careers?: boolean
}) {
  const { navigation, showContactUs, showCareers, showFamilyHub, contactForm } =
    mastheadNavigation

  return (
    <div
      className={cn('mx-auto flex h-full max-w-7xl items-center gap-6 px-5')}
    >
      <Link aria-label="Home" href={careers ? '/careers' : '/'}>
        <Image
          className="w-20"
          src={urlFor(logo).width(600).height(600).url()}
          width={600}
          height={600}
          alt={logo.altText || 'logo'}
        />
      </Link>
      <div className={cn('flex w-full items-center justify-end gap-8')}>
        <NavigationMenu viewport={false} className={cn('z-30')}>
          <NavigationMenuList>
            {navigation?.map((item, index) => {
              const itemId = (item as { _id?: string })._id
              const baseKey =
                itemId ||
                `nav-${item._type ?? 'item'}-${item.title ?? ''}-${index}`

              if (item._type === 'navigationItem') {
                return (
                  <NavigationMenuItem key={baseKey}>
                    <NavigationMenuLink
                      asChild
                      className={cn(
                        navigationMenuTriggerStyle(),
                        careersTriggerStyle
                      )}
                    >
                      <Link href={item?.url || ''}>{item.title}</Link>
                    </NavigationMenuLink>
                  </NavigationMenuItem>
                )
              }

              return (
                <NavigationMenuItem key={baseKey}>
                  <NavigationMenuTrigger
                    title={item.title || ''}
                    className={careersTriggerStyle}
                  >
                    {item.link?.url ? (
                      <Link href={item.link?.url || ''}>{item.title}</Link>
                    ) : (
                      <>{item.title}</>
                    )}
                  </NavigationMenuTrigger>
                  <NavigationMenuContent>
                    <div
                      className={cn(
                        'grid gap-2',
                        (item.items?.length ?? 0) > 4
                          ? 'grid-cols-2'
                          : 'grid-cols-1'
                      )}
                    >
                      {item.items?.map((subItem, subIndex) => {
                        const subItemId = (subItem as { _id?: string })._id
                        const subKey =
                          subItemId ||
                          `sub-${baseKey}-${subItem.title ?? ''}-${subIndex}`
                        return (
                          <NavigationMenuLink
                            key={subKey}
                            className="text-careers-blue-darker hover:bg-careers-green-light"
                            asChild
                          >
                            <Link href={subItem.url || ''}>
                              {subItem.title}
                            </Link>
                          </NavigationMenuLink>
                        )
                      })}
                    </div>
                  </NavigationMenuContent>
                </NavigationMenuItem>
              )
            })}
          </NavigationMenuList>
        </NavigationMenu>
        <div className="flex shrink-0 items-center gap-3">
          {showContactUs && contactForm && (
            <RegisterInterestForm
              formTitle={contactForm?.formTitle || ''}
              formDescription={contactForm?.formSubTitle || ''}
            >
              <Button
                size="sm"
                variant="inverse"
                className="rounded-full font-bold"
              >
                {contactForm?.ctaTitle || 'Apply today'}
              </Button>
            </RegisterInterestForm>
          )}
          {showFamilyHub && (
            <Link href="/the-family-hub/">
              <Button
                size="sm"
                variant="inverse"
                className="rounded-full font-bold"
              >
                The Family Hub
              </Button>
            </Link>
          )}
          {showCareers && (
            <>
              {isUkSite() && !careers ? (
                <a href="https://www.nfamilycareers.com/">
                  <Button
                    size="sm"
                    variant="green"
                    className="rounded-full font-bold"
                  >
                    Careers at N
                  </Button>
                </a>
              ) : (
                <>
                  {careers && (
                    <Button
                      size="sm"
                      variant="inverse"
                      className="rounded-full font-bold"
                      asChild
                    >
                      <Link
                        href={getSiteConfig().pinpoint.registerInterestUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Register Interest
                      </Link>
                    </Button>
                  )}
                  <Button
                    size="sm"
                    variant="green"
                    className="rounded-full font-bold"
                    asChild
                  >
                    <Link href={careers ? '/' : '/careers'}>
                      {careers ? 'N Family' : 'Careers at N'}
                    </Link>
                  </Button>
                </>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  )
}

export default function MastHeadRendererCareers({
  logo,
  mastheadNavigation,
  careers,
}: MastHeadRendererCareersProps) {
  if (!logo || !mastheadNavigation) return null

  return (
    <>
      <div className="hidden xl:block h-full">
        <MastHeadCareersDesktop
          logo={logo}
          mastheadNavigation={mastheadNavigation}
          careers={careers}
        />
      </div>
      <div className="block xl:hidden h-full">
        <MastHeadMobile
          logo={logo}
          mastheadNavigation={mastheadNavigation}
          careers={careers}
          variant="careers"
        />
      </div>
    </>
  )
}
